/**
 * Custom hooks for Data Trust & Quality API
 * Uses React Query for efficient data fetching and caching
 */

import { useQuery, useMutation, useQueryClient } from 'react-query'
import { DataQualityService } from '../services/dataQuality'
import { TimeWindow } from '../types/dataQuality'
import type {
  AggregateTrustScoreData,
  AggregateTrustScoreParams,
  SourceMetricsData,
  SourceMetricsParams,
  ErrorAnalysisData,
  ErrorAnalysisParams,
  SLAStatusData,
  SLAStatusParams,
  HistoricalTrendsData,
  HistoricalTrendsParams,
  SLAConfigurationRequest,
  SLAConfigurationResponse,
} from '../types/dataQuality'

/**
 * Hook for fetching aggregate trust score with KPI breakdown
 * @param params Query parameters including time window and quality threshold
 */
export const useAggregateTrustScore = (params: AggregateTrustScoreParams = {}) => {
  const { time_window, quality_threshold } = params

  return useQuery<AggregateTrustScoreData, Error>(
    ['aggregate-trust-score', { time_window, quality_threshold }],
    () => DataQualityService.getAggregateTrustScore(params),
    {
      staleTime: 30000, // Consider data fresh for 30 seconds
      cacheTime: 300000, // Keep data in cache for 5 minutes
      refetchOnWindowFocus: false,
      retry: 2,
      retryDelay: 1000,
    }
  )
}

/**
 * Hook for fetching detailed metrics for a specific data source
 * @param sourceId Data source ID
 * @param params Query parameters including time window
 * @param enabled Whether the query should run
 */
export const useSourceMetrics = (
  sourceId: number | null,
  params: SourceMetricsParams = {},
  enabled = true
) => {
  const { time_window } = params

  return useQuery<SourceMetricsData, Error>(
    ['source-metrics', sourceId, { time_window }],
    () => {
      if (!sourceId) throw new Error('Source ID is required')
      return DataQualityService.getSourceMetrics(sourceId, params)
    },
    {
      enabled: !!sourceId && enabled,
      staleTime: 30000, // Consider data fresh for 30 seconds
      cacheTime: 300000, // Keep data in cache for 5 minutes
      refetchOnWindowFocus: false,
      retry: 2,
      retryDelay: 1000,
    }
  )
}

/**
 * Hook for fetching error analysis across all data sources
 * @param params Query parameters including time window and error type filter
 */
export const useErrorAnalysis = (params: ErrorAnalysisParams = {}) => {
  const { time_window, error_type } = params

  return useQuery<ErrorAnalysisData, Error>(
    ['error-analysis', { time_window, error_type }],
    () => DataQualityService.getErrorAnalysis(params),
    {
      staleTime: 30000, // Consider data fresh for 30 seconds
      cacheTime: 300000, // Keep data in cache for 5 minutes
      refetchOnWindowFocus: false,
      retry: 2,
      retryDelay: 1000,
    }
  )
}

/**
 * Hook for fetching SLA compliance status and breach information
 * @param params Query parameters including data source ID and severity filter
 */
export const useSLAStatus = (params: SLAStatusParams = {}) => {
  const { data_source_id, severity } = params

  return useQuery<SLAStatusData, Error>(
    ['sla-status', { data_source_id, severity }],
    () => DataQualityService.getSLAStatus(params),
    {
      staleTime: 30000, // Consider data fresh for 30 seconds
      cacheTime: 300000, // Keep data in cache for 5 minutes
      refetchOnWindowFocus: false,
      retry: 2,
      retryDelay: 1000,
    }
  )
}

/**
 * Hook for fetching historical trend analysis
 * @param params Query parameters including metric type and days back
 * @param enabled Whether the query should run
 */
export const useHistoricalTrends = (params: HistoricalTrendsParams, enabled = true) => {
  const { metric_type, days_back } = params

  return useQuery<HistoricalTrendsData, Error>(
    ['historical-trends', { metric_type, days_back }],
    () => DataQualityService.getHistoricalTrends(params),
    {
      enabled: !!metric_type && enabled,
      staleTime: 60000, // Consider data fresh for 1 minute (historical data changes slowly)
      cacheTime: 600000, // Keep data in cache for 10 minutes
      refetchOnWindowFocus: false,
      retry: 2,
      retryDelay: 1000,
    }
  )
}

/**
 * Mutation hook for configuring SLA thresholds
 * Automatically invalidates related queries on success
 */
export const useConfigureSLA = () => {
  const queryClient = useQueryClient()

  return useMutation<SLAConfigurationResponse, Error, SLAConfigurationRequest>(
    (config) => DataQualityService.configureSLAThresholds(config),
    {
      onSuccess: (data) => {
        // Invalidate and refetch related queries
        queryClient.invalidateQueries(['sla-status'])
        queryClient.invalidateQueries(['source-metrics', data.data_source_id])
        queryClient.invalidateQueries(['aggregate-trust-score'])
      },
      retry: 1,
      retryDelay: 1000,
    }
  )
}

/**
 * Hook for fetching multiple source metrics at once
 * Useful for dashboard views showing multiple sources
 * @param sourceIds Array of data source IDs
 * @param timeWindow Time window for all sources
 */
export const useMultipleSourceMetrics = (
  sourceIds: number[],
  timeWindow: TimeWindow = TimeWindow.WEEK
) => {
  return useQuery<SourceMetricsData[], Error>(
    ['multiple-source-metrics', sourceIds, timeWindow],
    async () => {
      const promises = sourceIds.map((sourceId) =>
        DataQualityService.getSourceMetrics(sourceId, { time_window: timeWindow })
          .catch((error) => {
            console.warn(`Failed to fetch metrics for source ${sourceId}:`, error.message)
            return null
          })
      )
      const results = await Promise.all(promises)
      // Filter out null values (failed requests) and return only successful ones
      return results.filter((result): result is SourceMetricsData => result !== null)
    },
    {
      enabled: sourceIds.length > 0,
      staleTime: 30000,
      cacheTime: 300000,
      refetchOnWindowFocus: false,
      retry: 2,
      retryDelay: 1000,
    }
  )
}

/**
 * Hook for fetching dashboard summary with trends
 * @param params Query parameters including time window and comparison days
 */
export const useDashboardSummary = (params: import('../types/dataQuality').DashboardSummaryParams = {}) => {
  const { time_window, comparison_days } = params

  return useQuery<import('../types/dataQuality').DashboardSummaryData, Error>(
    ['dashboard-summary', { time_window, comparison_days }],
    () => DataQualityService.getDashboardSummary(params),
    {
      staleTime: 30000,
      cacheTime: 300000,
      refetchOnWindowFocus: false,
      retry: 2,
      retryDelay: 1000,
    }
  )
}

/**
 * Hook for fetching pipeline health metrics
 * @param params Query parameters for filtering
 */
export const usePipelineHealth = (params: import('../types/dataQuality').PipelineHealthParams = {}) => {
  const { processing_job_id, data_source_id, time_window } = params

  return useQuery<import('../types/dataQuality').PipelineHealthData, Error>(
    ['pipeline-health', { processing_job_id, data_source_id, time_window }],
    () => DataQualityService.getPipelineHealth(params),
    {
      staleTime: 30000,
      cacheTime: 300000,
      refetchOnWindowFocus: false,
      retry: 2,
      retryDelay: 1000,
    }
  )
}

/**
 * Hook for fetching schema drift analysis
 * @param sourceId Data source ID
 * @param params Query parameters for filtering
 * @param enabled Whether the query should run
 */
export const useSchemaDrift = (
  sourceId: number | null,
  params: import('../types/dataQuality').SchemaDriftParams = {},
  enabled = true
) => {
  const { time_window } = params

  return useQuery<import('../types/dataQuality').SchemaDriftData, Error>(
    ['schema-drift', sourceId, { time_window }],
    () => {
      if (!sourceId) throw new Error('Source ID is required')
      return DataQualityService.getSchemaDrift(sourceId, params)
    },
    {
      enabled: !!sourceId && enabled,
      staleTime: 60000,
      cacheTime: 600000,
      refetchOnWindowFocus: false,
      retry: 2,
      retryDelay: 1000,
    }
  )
}

/**
 * Hook for fetching schema status summary
 * @param params Query parameters for filtering
 */
export const useSchemaStatusSummary = (params: import('../types/dataQuality').SchemaStatusSummaryParams = {}) => {
  const { data_source_ids } = params

  return useQuery<import('../types/dataQuality').SchemaStatusSummaryData, Error>(
    ['schema-status-summary', { data_source_ids }],
    () => DataQualityService.getSchemaStatusSummary(params),
    {
      staleTime: 60000,
      cacheTime: 600000,
      refetchOnWindowFocus: false,
      retry: 2,
      retryDelay: 1000,
    }
  )
}

/**
 * Hook for fetching source metrics with trend percentages
 * @param sourceId Data source ID
 * @param params Query parameters for filtering
 * @param enabled Whether the query should run
 */
export const useSourceMetricsWithTrends = (
  sourceId: number | null,
  params: import('../types/dataQuality').SourceMetricsWithTrendsParams = {},
  enabled = true
) => {
  const {
    comparison_days,
    include_detailed_trends,
    profile,
    error_time_series_days
  } = params

  return useQuery<import('../types/dataQuality').SourceMetricsWithTrendsData, Error>(
    ['source-metrics-with-trends', sourceId, {
      comparison_days,
      include_detailed_trends,
      profile,
      error_time_series_days
    }],
    () => {
      if (!sourceId) throw new Error('Source ID is required')
      return DataQualityService.getSourceMetricsWithTrends(sourceId, params)
    },
    {
      enabled: !!sourceId && enabled,
      staleTime: 30000,
      cacheTime: 300000,
      refetchOnWindowFocus: false,
      retry: 2,
      retryDelay: 1000,
    }
  )
}

/**
 * Hook for fetching time-series trend data
 * @param params Query parameters including metric type
 * @param enabled Whether the query should run
 */
export const useTimeSeriesTrend = (
  params: import('../types/dataQuality').TimeSeriesTrendParams,
  enabled = true
) => {
  const { metric_type, data_source_id, days_back, granularity, include_trend_line } = params

  return useQuery<import('../types/dataQuality').TimeSeriesTrendData, Error>(
    ['time-series-trend', { metric_type, data_source_id, days_back, granularity, include_trend_line }],
    () => DataQualityService.getTimeSeriesTrend(params),
    {
      enabled: !!metric_type && enabled,
      staleTime: 60000,
      cacheTime: 600000,
      refetchOnWindowFocus: false,
      retry: 2,
      retryDelay: 1000,
    }
  )
}

/**
 * Hook for fetching source reliability table
 * @param params Query parameters for filtering
 */
export const useSourceReliabilityTable = (params: import('../types/dataQuality').SourceReliabilityTableParams = {}) => {
  const { time_window, limit } = params

  return useQuery<import('../types/dataQuality').SourceReliabilityTableData, Error>(
    ['source-reliability-table', { time_window, limit }],
    () => DataQualityService.getSourceReliabilityTable(params),
    {
      staleTime: 30000,
      cacheTime: 300000,
      refetchOnWindowFocus: false,
      retry: 2,
      retryDelay: 1000,
    }
  )
}

/**
 * Hook for fetching active alerts
 * @param params Query parameters for filtering
 */
export const useActiveAlerts = (params: import('../types/dataQuality').ActiveAlertsParams = {}) => {
  const { data_source_id, severity, alert_type, limit, offset } = params

  return useQuery<import('../types/dataQuality').ActiveAlertsData, Error>(
    ['active-alerts', { data_source_id, severity, alert_type, limit, offset }],
    () => DataQualityService.getActiveAlerts(params),
    {
      staleTime: 20000, // Alerts should refresh frequently
      cacheTime: 120000,
      refetchOnWindowFocus: true, // Refetch when user returns to window
      retry: 2,
      retryDelay: 1000,
    }
  )
}
